import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Surface } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

export const FIELD_TYPES = [
  { value: "TEXTO", label: "Texto" },
  { value: "COR", label: "Cor (filamento)" },
  { value: "SELECAO", label: "Seleção" },
];

export const typeLabel = (t) => FIELD_TYPES.find((x) => x.value === t)?.label || t;

/** Biblioteca de campos de personalização reaproveitáveis pelos produtos. */
export function CustomFieldsLibrary() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .get("/custom-fields")
      .then(({ data }) => setList(data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const remove = async (f) => {
    try {
      await api.delete(`/custom-fields/${f.id}`);
      toast.success("Campo removido da biblioteca. Produtos e pedidos não mudam.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Surface className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg">Campos de personalização</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Crie os campos uma vez (Nome, Tema, Peso, Cor 1...) e marque em cada produto
            personalizável.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setEditing({})}
          data-testid="add-custom-field-btn"
          className="h-9 shrink-0 gap-1.5 rounded-full text-xs font-semibold"
        >
          <Plus className="h-3.5 w-3.5" /> Novo campo
        </Button>
      </div>

      {list === null ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum campo criado ainda.</p>
      ) : (
        <div className="space-y-2">
          {list.map((f) => (
            <div
              key={f.id}
              className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-white/10 bg-secondary/40 p-3"
              data-testid={`custom-field-row-${f.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{f.name}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {typeLabel(f.type)}
                  {f.required ? " · obrigatório" : " · opcional"}
                  {f.type === "TEXTO" ? ` · até ${f.max_length} caracteres` : ""}
                  {f.type === "SELECAO" ? ` · ${(f.options || []).join(", ")}` : ""}
                  {f.type === "COR" ? ` · ${(f.filament_ids || []).length} cores` : ""}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(f)}
                data-testid={`edit-custom-field-${f.id}`}
                className="h-8 px-2 text-muted-foreground hover:text-foreground"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(f)}
                data-testid={`delete-custom-field-${f.id}`}
                className="h-8 px-2 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CustomFieldModal
          field={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </Surface>
  );
}

function CustomFieldModal({ field, onClose, onSaved }) {
  const [name, setName] = useState(field.name || "");
  const [type, setType] = useState(field.type || "TEXTO");
  const [required, setRequired] = useState(field.required !== false);
  const [maxLength, setMaxLength] = useState(String(field.max_length || 40));
  const [options, setOptions] = useState((field.options || []).join(", "));
  const [filaments, setFilaments] = useState([]);
  const [picked, setPicked] = useState(field.filament_ids || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/filaments")
      .then(({ data }) => setFilaments(data.filter((f) => f.active !== false)))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        type,
        required,
        max_length: Number(maxLength) || 40,
        options: type === "SELECAO"
          ? options.split(",").map((o) => o.trim()).filter(Boolean)
          : [],
        filament_ids: type === "COR" ? picked : [],
      };
      if (field.id) await api.put(`/custom-fields/${field.id}`, body);
      else await api.post("/custom-fields", body);
      toast.success("Campo salvo.");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={field.id ? "Editar campo" : "Novo campo de personalização"}
      testid="custom-field-modal"
    >
      <form onSubmit={submit} className="mt-2 flex w-full min-w-0 flex-col gap-4">
        <Field label="Nome do campo">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Nome, Tema, Peso, Cor 1"
            required
            data-testid="custom-field-name"
          />
        </Field>
        <Field label="Tipo">
          <NativeSelect
            value={type}
            onChange={setType}
            options={FIELD_TYPES}
            data-testid="custom-field-type"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            className="h-4 w-4 accent-brand"
            data-testid="custom-field-required"
          />
          Obrigatório para o cliente
        </label>
        {type === "TEXTO" && (
          <Field label="Máximo de caracteres">
            <Input
              type="number"
              min="1"
              max="200"
              value={maxLength}
              onChange={(e) => setMaxLength(e.target.value)}
              className="font-num"
              data-testid="custom-field-max-length"
            />
          </Field>
        )}
        {type === "SELECAO" && (
          <Field label="Opções" hint="Separe por vírgula">
            <Input
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              placeholder="Princesa, Futebol, Safari"
              data-testid="custom-field-options"
            />
          </Field>
        )}
        {type === "COR" && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cores disponíveis (filamentos)
            </span>
            <div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
              {filaments.map((f) => {
                const on = picked.includes(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() =>
                      setPicked((prev) =>
                        on ? prev.filter((x) => x !== f.id) : [...prev, f.id]
                      )
                    }
                    data-testid={`custom-field-filament-${f.id}`}
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                      on
                        ? "border-brand bg-brand/15 text-brand"
                        : "border-white/15 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {f.color_name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="h-11 gap-2 rounded-full font-semibold"
          data-testid="save-custom-field-btn"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar campo
        </Button>
      </form>
    </ResponsiveModal>
  );
}
